/**
 * Exposes React, Remotion, and other runtime modules on the global
 * `window.__CASCADE_MODULES__` map so that dynamically compiled scene
 * code can resolve its imports at runtime.
 *
 * This is necessary because esbuild-wasm compiles TSX to ESM with
 * bare imports (e.g. `import { useState } from "react"`), which the
 * browser can't resolve natively. The compiler rewrites these to
 * read from this global map instead.
 */

import * as React from 'react';
import * as ReactJsxRuntime from 'react/jsx-runtime';
import * as Remotion from 'remotion';

declare global {
  interface Window {
    __CASCADE_MODULES__: Record<string, unknown>;
  }
}

export function setupModuleMap(): void {
  window.__CASCADE_MODULES__ = {
    react: React,
    'react/jsx-runtime': ReactJsxRuntime,
    remotion: Remotion,
  };

  // Lazily add @remotion/* subpackages as they're imported
  const handler: ProxyHandler<Record<string, unknown>> = {
    get(target, prop: string) {
      if (prop in target) return target[prop];

      // Auto-resolve @remotion/* packages
      if (prop.startsWith('@remotion/')) {
        try {
          // These will be bundled by Vite since they're in the dependency graph
          return undefined; // Will be populated by dynamic imports in the compiler
        } catch {
          return undefined;
        }
      }

      return target[prop];
    },
  };

  window.__CASCADE_MODULES__ = new Proxy(
    window.__CASCADE_MODULES__,
    handler,
  );
}
